import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type TipoToast = 'sucesso' | 'erro' | 'aviso' | 'info';

interface Toast {
  id: number;
  tipo: TipoToast;
  mensagem: string;
}

interface ToastCtx {
  mostrar: (mensagem: string, tipo?: TipoToast) => void;
  sucesso: (m: string) => void;
  erro: (m: string) => void;
  aviso: (m: string) => void;
}

const ToastContext = createContext<ToastCtx | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const mostrar = useCallback((mensagem: string, tipo: TipoToast = 'info') => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, tipo, mensagem }]);
    // Erros longos/multilinha precisam mais tempo pra ler.
    const longo = tipo === 'erro' && (mensagem.length > 80 || mensagem.includes('\n'));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), longo ? 8000 : 4000);
  }, []);

  const sucesso = useCallback((m: string) => mostrar(m, 'sucesso'), [mostrar]);
  const erro = useCallback((m: string) => mostrar(m, 'erro'), [mostrar]);
  const aviso = useCallback((m: string) => mostrar(m, 'aviso'), [mostrar]);

  // Valor memoizado: sem isso, mostrar um toast recria o objeto do contexto e
  // qualquer useEffect que dependa de `toast` re-dispara (ex: recarregava o
  // produto e apagava o rascunho gerado pela IA no ProdutoForm).
  const valor = useMemo(() => ({ mostrar, sucesso, erro, aviso }), [mostrar, sucesso, erro, aviso]);

  return (
    <ToastContext.Provider value={valor}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.tipo}`} style={{ whiteSpace: 'pre-wrap' }}>{t.mensagem}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast fora de ToastProvider');
  return ctx;
}
